import { MailOutline } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  ListItem,
  ListItemButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Chart } from 'chart.js';
import { TFunction } from 'i18next';
import { LngLatBounds } from 'maplibre-gl';
import moment from 'moment';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../app/context';

import {
  countingWeeklyWindowWeeks,
  getCountingKpi,
  getCountingSiteWeeklySeries,
  getCountingSourceData,
  getCountingWeeklySeries,
  ISourceResult,
} from './data/service';
import { countingSources } from './data/sources';
import {
  ICountingChannelWeeklySeries,
  ICountingKpi,
  ICountingSite,
  ICountingWeeklyPoint,
} from './types';

const emptyKpi: ICountingKpi = {
  count: 0,
  countedAt: null,
  sourceNames: [],
  territories: [],
};

export function Counts(): React.JSX.Element {
  const {
    map: { current: map },
    counts: { loading, selectedSiteId, sites, sources },
    actions: {
      setCountingLoading,
      setCountingSites,
      setCountingSources,
      setSelectedCountingSiteId,
    },
  } = useContext(AppContext);
  const [kpi, setKpi] = useState<ICountingKpi>(emptyKpi);
  const [weeklySeries, setWeeklySeries] = useState<ICountingWeeklyPoint[]>([]);
  const [weeklySeriesLoading, setWeeklySeriesLoading] = useState(false);
  const [weeklySeriesFailedSources, setWeeklySeriesFailedSources] = useState<string[]>([]);
  const [weeklySeriesError, setWeeklySeriesError] = useState(false);
  const [selectedSiteSeries, setSelectedSiteSeries] = useState<ICountingChannelWeeklySeries[]>([]);
  const [selectedSiteSeriesLoading, setSelectedSiteSeriesLoading] = useState(false);
  const [selectedSiteSeriesError, setSelectedSiteSeriesError] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const loadCountsRequestRef = useRef(0);
  const selectedSiteSeriesRequestRef = useRef(0);
  const { t } = useTranslation();
  const contactHref = `mailto:team-data@geovelo.fr?subject=${encodeURIComponent(
    t('bicycle_facilities.counts.contact.subject'),
  )}&body=${encodeURIComponent(t('bicycle_facilities.counts.contact.body'))}`;

  useEffect(() => {
    loadCounts();
  }, []);

  const territoryRows = useMemo(() => {
    return sources
      .map((source) => {
        const territorySites = sites.filter((site) => site.sourceId === source.id);
        const count = territorySites.reduce((sum, site) => sum + site.count, 0);
        const sitesCount = territorySites.length;
        const countedAt = source.latestCountedAt || null;

        return {
          count,
          countedAt,
          enabled: source.enabled,
          error: source.error,
          freshness: countedAt ? getFreshnessFromDate(countedAt) : ('error' as const),
          id: source.id,
          loading: source.loading,
          name: source.cityName,
          sitesCount,
        };
      })
      .sort(
        (a, b) =>
          Number(a.loading) - Number(b.loading) ||
          b.count - a.count ||
          a.name.localeCompare(b.name),
      );
  }, [sites, sources]);

  const selectedSite = sites.find(({ id }) => id === selectedSiteId) || null;

  useEffect(() => {
    if (!selectedSite) {
      setSelectedSiteSeries([]);
      setSelectedSiteSeriesLoading(false);
      setSelectedSiteSeriesError(false);

      return;
    }

    const source = countingSources.find(({ id }) => id === selectedSite.sourceId);
    if (!source) {
      setSelectedSiteSeries([]);
      setSelectedSiteSeriesLoading(false);
      setSelectedSiteSeriesError(true);

      return;
    }

    const requestId = selectedSiteSeriesRequestRef.current + 1;
    selectedSiteSeriesRequestRef.current = requestId;
    setSelectedSiteSeries([]);
    setSelectedSiteSeriesLoading(true);
    setSelectedSiteSeriesError(false);

    getCountingSiteWeeklySeries(source, selectedSite)
      .then((series) => {
        if (requestId !== selectedSiteSeriesRequestRef.current) return;

        setSelectedSiteSeries(series);
      })
      .catch((err) => {
        console.error('[Counts][selectedSiteSeries]', err);
        if (requestId !== selectedSiteSeriesRequestRef.current) return;

        setSelectedSiteSeriesError(true);
      })
      .finally(() => {
        if (requestId === selectedSiteSeriesRequestRef.current) {
          setSelectedSiteSeriesLoading(false);
        }
      });
  }, [selectedSite]);

  async function loadCounts(): Promise<void> {
    const requestId = loadCountsRequestRef.current + 1;
    loadCountsRequestRef.current = requestId;
    const initialSources = countingSources.map(({ id, name, cityName, enabled }) => ({
      id,
      name,
      cityName,
      enabled,
      loading: enabled,
      sitesCount: 0,
      channelsCount: 0,
    }));

    setCountingLoading(true);
    setCountingSites([]);
    setKpi(emptyKpi);
    setCountingSources(initialSources);
    setWeeklySeries([]);
    setWeeklySeriesLoading(true);
    setWeeklySeriesFailedSources([]);
    setWeeklySeriesError(false);

    try {
      const weeklySeriesPromise = getCountingWeeklySeries(countingSources)
        .then((result) => {
          if (requestId !== loadCountsRequestRef.current) return;

          setWeeklySeries(result.points);
          setWeeklySeriesFailedSources(result.failedSources);
        })
        .catch((err) => {
          console.error('[Counts][weeklySeries]', err);
          if (requestId !== loadCountsRequestRef.current) return;

          setWeeklySeriesError(true);
        })
        .finally(() => {
          if (requestId === loadCountsRequestRef.current) setWeeklySeriesLoading(false);
        });
      const resultsMap = new Map<string, ISourceResult>();

      await Promise.all(
        countingSources.map(async (source) => {
          const result = await getCountingSourceData(source);
          if (requestId !== loadCountsRequestRef.current) return;

          resultsMap.set(source.id, result);

          const orderedResults = countingSources
            .map(({ id }) => resultsMap.get(id))
            .filter((value): value is ISourceResult => Boolean(value));

          setCountingSources(
            initialSources.map(
              (item) =>
                orderedResults.find(({ source: sourceState }) => sourceState.id === item.id)
                  ?.source || item,
            ),
          );
          setCountingSites(orderedResults.flatMap(({ sites: resultSites }) => resultSites));
        }),
      );

      if (requestId !== loadCountsRequestRef.current) return;

      const orderedResults = countingSources
        .map(({ id }) => resultsMap.get(id))
        .filter((value): value is ISourceResult => Boolean(value));

      setKpi(await getCountingKpi(countingSources, orderedResults));
      await weeklySeriesPromise;
    } catch (err) {
      console.error(err);
    } finally {
      if (requestId === loadCountsRequestRef.current) setCountingLoading(false);
    }
  }

  function handleTerritorySelect(sourceId: string): void {
    const nextSelectedSourceId = selectedSourceId === sourceId ? null : sourceId;
    const territorySites = sites.filter(({ sourceId: siteSourceId }) => siteSourceId === sourceId);

    setSelectedSourceId(nextSelectedSourceId);
    setSelectedCountingSiteId(null);

    if (nextSelectedSourceId && territorySites.length > 0) {
      focusSites(territorySites);
    }
  }

  function focusSites(targetSites: ICountingSite[]): void {
    if (!map || targetSites.length <= 0) return;

    if (targetSites.length === 1) {
      map.flyTo({
        center: targetSites[0].coordinates,
        essential: true,
        zoom: Math.max(map.getZoom(), 14),
      });

      return;
    }

    const bounds = targetSites.reduce(
      (res, { coordinates: [lng, lat] }) => res.extend([lng, lat]),
      new LngLatBounds(targetSites[0].coordinates, targetSites[0].coordinates),
    );

    map.fitBounds(bounds, {
      essential: true,
      padding: 48,
    });
  }

  function closeSelectedSiteView(): void {
    setSelectedCountingSiteId(null);
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} padding={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography fontWeight={700} variant="h4">
                {loading ? <CircularProgress size={28} /> : kpi.count.toLocaleString()}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {formatKpiTitle(kpi, t)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {sources.some(({ error }) => error) && (
        <Alert severity="warning">{t('bicycle_facilities.counts.sources.partial_error')}</Alert>
      )}

      <Card variant="outlined">
        <CardContent
          sx={{
            background:
              'linear-gradient(135deg, rgba(50, 106, 194, 0.08), rgba(42, 198, 130, 0.08))',
          }}
        >
          <Stack
            alignItems={{ md: 'center', xs: 'flex-start' }}
            direction={{ md: 'row', xs: 'column' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography gutterBottom color="primary.main" fontWeight={700} variant="subtitle2">
                {t('bicycle_facilities.counts.contact.title')}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {t('bicycle_facilities.counts.contact.description')}
              </Typography>
            </Box>
            <Button
              color="primary"
              component="a"
              href={contactHref}
              size="medium"
              startIcon={<MailOutline />}
              sx={{
                borderRadius: 2,
                flexShrink: 0,
                fontWeight: 700,
                px: 2,
                py: 1,
                textTransform: 'none',
              }}
              variant="contained"
            >
              {t('bicycle_facilities.counts.contact.cta')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {selectedSite ? (
        <SelectedSiteEvolutionCard
          error={selectedSiteSeriesError}
          loading={selectedSiteSeriesLoading}
          onClose={closeSelectedSiteView}
          series={selectedSiteSeries}
          site={selectedSite}
        />
      ) : (
        <CountsEvolutionCard
          error={weeklySeriesError}
          failedSources={weeklySeriesFailedSources}
          loading={weeklySeriesLoading}
          points={weeklySeries}
        />
      )}

      <Card variant="outlined">
        <CardContent sx={{ paddingY: 1.5, '&:last-child': { paddingBottom: 1.5 } }}>
          <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
            <Typography color="text.secondary" variant="body2">
              {t('bicycle_facilities.counts.territories.label')}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {selectedSourceId
                ? t('bicycle_facilities.counts.territories.selected', {
                    name: territoryRows.find(({ id }) => id === selectedSourceId)?.name || '',
                  })
                : t('bicycle_facilities.counts.territories.all')}
            </Typography>
          </Stack>
          <Stack spacing={1.25} sx={{ marginTop: 1 }}>
            {territoryRows.map((territory) => (
              <TerritoryRow
                key={territory.id}
                onSelect={() => territory.enabled && handleTerritorySelect(territory.id)}
                selected={selectedSourceId === territory.id}
                territory={territory}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {selectedSite && (
        <Card variant="outlined">
          <CardContent>
            <Typography gutterBottom fontWeight={700}>
              {selectedSite.name}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {selectedSite.cityName} · {formatDate(selectedSite.countedAt)}
            </Typography>
            <Divider sx={{ marginY: 1 }} />
            {selectedSite.channels.map((channel) => (
              <Box key={channel.id} paddingY={0.5}>
                <Typography variant="body2">
                  {channel.name} · {channel.count.toLocaleString()}
                </Typography>
                {channel.anomalyProbability !== undefined &&
                  channel.anomalyProbability !== null && (
                    <Typography color="warning.main" variant="caption">
                      {t('bicycle_facilities.counts.anomaly_probability', {
                        value: channel.anomalyProbability,
                      })}
                    </Typography>
                  )}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function CountsEvolutionCard({
  error,
  failedSources,
  loading,
  points,
}: {
  error: boolean;
  failedSources: string[];
  loading: boolean;
  points: ICountingWeeklyPoint[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const hasData = points.some(({ sourceNames }) => sourceNames.length > 0);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={2}>
          <Box>
            <Typography fontWeight={700} variant="h6">
              {t('bicycle_facilities.counts.evolution.title')}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {t('bicycle_facilities.counts.evolution.subtitle', {
                count: countingWeeklyWindowWeeks,
              })}
            </Typography>
          </Box>

          {failedSources.length > 0 && (
            <Alert severity={hasData ? 'warning' : 'error'}>
              {t('bicycle_facilities.counts.evolution.partial_error', {
                sources: failedSources.join(', '),
              })}
            </Alert>
          )}

          {error && !hasData && (
            <Alert severity="error">{t('bicycle_facilities.counts.evolution.request_error')}</Alert>
          )}

          {loading ? (
            <Box alignItems="center" display="flex" gap={1.5} minHeight={240}>
              <CircularProgress size={24} />
              <Typography color="text.secondary" variant="body2">
                {t('bicycle_facilities.counts.evolution.loading')}
              </Typography>
            </Box>
          ) : hasData ? (
            <CountsEvolutionChart points={points} />
          ) : (
            <Box minHeight={240}>
              <Typography color="text.secondary" variant="body2">
                {t('bicycle_facilities.counts.evolution.empty')}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function CountsEvolutionChart({ points }: { points: ICountingWeeklyPoint[] }): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!canvasRef.current) return;

    const cityNames = Array.from(
      new Set(
        points
          .flatMap(({ cityCounts }) => Object.keys(cityCounts))
          .sort((a, b) => a.localeCompare(b)),
      ),
    );
    const palette = [
      '#2e7d32',
      '#ed6c02',
      '#9c27b0',
      '#00838f',
      '#d32f2f',
      '#5d4037',
      '#1565c0',
      '#6a1b9a',
    ];

    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels: points.map(({ weekStart }) => moment(weekStart).format('DD MMM')),
        datasets: [
          {
            label: t('bicycle_facilities.counts.evolution.series_label'),
            data: points.map(({ count }) => count),
            backgroundColor: 'rgba(25, 118, 210, 0.16)',
            borderColor: '#1976d2',
            borderWidth: 3,
            fill: false,
            pointRadius: 2,
            tension: 0.25,
          },
          ...cityNames.map((cityName, index) => ({
            label: cityName,
            data: points.map(({ cityCounts }) => cityCounts[cityName] || 0),
            backgroundColor: `${palette[index % palette.length]}33`,
            borderColor: palette[index % palette.length],
            borderWidth: 2,
            fill: false,
            pointRadius: 1.5,
            tension: 0.25,
          })),
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            ticks: {
              maxRotation: 0,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) => Number(value).toLocaleString(),
            },
          },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          legend: {
            display: true,
          },
          tooltip: {
            callbacks: {
              title: ([{ dataIndex }]) => {
                const point = points[dataIndex];
                const weekStart = moment(point.weekStart);
                const weekEnd = weekStart.clone().add(6, 'day');

                return t('bicycle_facilities.counts.evolution.tooltip_title', {
                  end: weekEnd.format('L'),
                  start: weekStart.format('L'),
                });
              },
              label: ({ dataset, parsed: { y } }) =>
                `${dataset.label}: ${Number(y).toLocaleString()}`,
              afterBody: ([{ dataIndex }]) =>
                points[dataIndex].sourceNames.length
                  ? t('bicycle_facilities.counts.evolution.tooltip_sources', {
                      count: points[dataIndex].sourceNames.length,
                    })
                  : '',
            },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [points, t]);

  return (
    <Box minHeight={240} position="relative">
      <canvas ref={canvasRef} />
    </Box>
  );
}

function SelectedSiteEvolutionCard({
  error,
  loading,
  onClose,
  series,
  site,
}: {
  error: boolean;
  loading: boolean;
  onClose: () => void;
  series: ICountingChannelWeeklySeries[];
  site: ICountingSite;
}): React.JSX.Element {
  const { t } = useTranslation();
  const hasData = series.some(({ points }) => points.some(({ count }) => count > 0));

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={1.5}>
          <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography fontWeight={700} variant="h6">
                {site.name}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {site.cityName} · {t('bicycle_facilities.counts.site_evolution.title')}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {t('bicycle_facilities.counts.site_evolution.subtitle', {
                  count: countingWeeklyWindowWeeks,
                })}
              </Typography>
            </Box>
            <Button onClick={onClose} size="small" variant="outlined">
              {t('commons.actions.close')}
            </Button>
          </Stack>

          {error && !hasData && (
            <Alert severity="error">
              {t('bicycle_facilities.counts.site_evolution.request_error')}
            </Alert>
          )}

          {loading ? (
            <Box alignItems="center" display="flex" gap={1.5} minHeight={220}>
              <CircularProgress size={20} />
              <Typography color="text.secondary" variant="body2">
                {t('bicycle_facilities.counts.site_evolution.loading')}
              </Typography>
            </Box>
          ) : hasData ? (
            <SelectedSiteEvolutionChart series={series} />
          ) : (
            <Typography color="text.secondary" minHeight={220} variant="body2">
              {t('bicycle_facilities.counts.site_evolution.empty')}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function SelectedSiteEvolutionChart({
  series,
}: {
  series: ICountingChannelWeeklySeries[];
}): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!canvasRef.current || series.length <= 0) return;

    const labels =
      series[0]?.points.map(({ weekStart }) => moment(weekStart).format('DD MMM')) || [];
    const palette = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#00838f', '#d32f2f'];

    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: series.map((dataset, index) => ({
          label: dataset.label,
          data: dataset.points.map(({ count }) => count),
          borderColor: palette[index % palette.length],
          backgroundColor: `${palette[index % palette.length]}33`,
          pointRadius: 2,
          tension: 0.25,
          fill: false,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            ticks: {
              maxRotation: 0,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) => Number(value).toLocaleString(),
            },
          },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: ([{ dataIndex }]) => {
                const point = series[0]?.points[dataIndex];
                if (!point) return '';

                const weekStart = moment(point.weekStart);
                const weekEnd = weekStart.clone().add(6, 'day');

                return t('bicycle_facilities.counts.evolution.tooltip_title', {
                  end: weekEnd.format('L'),
                  start: weekStart.format('L'),
                });
              },
              label: ({ dataset, parsed: { y } }) =>
                `${dataset.label}: ${Number(y).toLocaleString()}`,
            },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [series, t]);

  return (
    <Box minHeight={220} position="relative">
      <canvas ref={canvasRef} />
    </Box>
  );
}

function TerritoryRow({
  onSelect,
  selected,
  territory,
}: {
  onSelect: () => void;
  selected: boolean;
  territory: {
    id: string;
    name: string;
    count: number;
    countedAt: string | null;
    freshness: 'fresh' | 'late' | 'stale' | 'error';
    enabled: boolean;
    error?: string;
    loading: boolean;
    sitesCount: number;
  };
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <ListItem disablePadding>
      <ListItemButton
        disabled={!territory.enabled}
        onClick={onSelect}
        selected={selected}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          columnGap: 1.25,
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1.15fr) minmax(72px, 1fr) auto',
            md: 'minmax(0, 1.2fr) minmax(96px, 1fr) auto auto auto',
          },
          paddingY: 1.1,
        }}
      >
        <Typography
          fontWeight={700}
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          variant="body2"
        >
          {territory.name}
        </Typography>
        {territory.loading ? (
          <>
            <LinearProgress
              aria-hidden
              sx={{
                backgroundColor: '#edf0f2',
                borderRadius: 999,
                gridColumn: { xs: '2 / 4', md: '2 / 6' },
                height: 6,
                minWidth: 0,
                placeSelf: 'center stretch',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#1976d2',
                },
              }}
              variant="indeterminate"
            />
          </>
        ) : (
          <>
            <Box
              alignItems="center"
              display="flex"
              gap={0.75}
              justifyContent="flex-start"
              sx={{ minWidth: 0, whiteSpace: 'nowrap' }}
            >
              <FreshnessMarker freshness={territory.freshness} />
              <Typography color="text.secondary" variant="caption">
                {territory.countedAt
                  ? formatDate(territory.countedAt)
                  : t('bicycle_facilities.counts.kpi.empty')}
              </Typography>
            </Box>
            <Typography
              fontWeight={700}
              sx={{
                textAlign: 'right',
                whiteSpace: 'nowrap',
              }}
              variant="body2"
            >
              {territory.count.toLocaleString()}
            </Typography>
            <Typography
              color={territory.error ? 'error.main' : 'text.secondary'}
              sx={{
                display: { xs: 'none', md: 'block' },
                textAlign: 'right',
                whiteSpace: 'nowrap',
              }}
              variant="caption"
            >
              {t(
                territory.sitesCount > 1
                  ? 'bicycle_facilities.counts.counters.other'
                  : 'bicycle_facilities.counts.counters.one',
                {
                  count: territory.sitesCount,
                },
              )}
            </Typography>
          </>
        )}
      </ListItemButton>
    </ListItem>
  );
}

function formatKpiTitle({ countedAt }: ICountingKpi, t: TFunction): string {
  if (!countedAt) return t('bicycle_facilities.counts.kpi.counted');

  return t('bicycle_facilities.counts.kpi.counted_on', { date: formatDate(countedAt) });
}

function formatDate(date: string): string {
  return moment(date).format('LL');
}

function FreshnessMarker({
  freshness,
}: {
  freshness: 'fresh' | 'late' | 'stale' | 'error';
}): React.JSX.Element {
  const { t } = useTranslation();
  const config = getFreshnessConfig(freshness, t);

  return (
    <Tooltip arrow title={config.label}>
      <Box
        aria-label={config.label}
        bgcolor={config.color}
        borderRadius="50%"
        component="span"
        display="inline-block"
        flex="0 0 auto"
        height={8}
        width={8}
      />
    </Tooltip>
  );
}

function getFreshnessConfig(
  freshness: 'fresh' | 'late' | 'stale' | 'error',
  t: TFunction,
): {
  color: string;
  label: string;
} {
  if (freshness === 'fresh')
    return { color: '#2e7d32', label: t('bicycle_facilities.counts.legend.fresh') };
  if (freshness === 'late')
    return { color: '#ed6c02', label: t('bicycle_facilities.counts.legend.late') };
  if (freshness === 'stale')
    return { color: '#d32f2f', label: t('bicycle_facilities.counts.legend.stale') };

  return { color: '#546e7a', label: t('bicycle_facilities.counts.legend.error') };
}

function getFreshnessFromDate(countedAt: string): 'fresh' | 'late' | 'stale' {
  const age = moment().diff(moment(countedAt), 'months', true);
  if (age <= 1) return 'fresh';
  if (age <= 12) return 'late';

  return 'stale';
}
